// ============= Imports =============

import {
  Action as WhatToDoAction,
  Feature as WhatToDoFeature,
  type ResultOption,
} from '@/apis/core/search/whatToDo/post/postWhatToDo';
import { LoadingSpinner } from '@tw/tw-component-library';
import {
  FiBriefcase,
  FiEdit3,
  FiFileText,
  FiFolder,
  FiGitBranch,
  FiLayers,
  FiLock,
  FiMessageCircle,
  FiMessageSquare,
  FiPlusCircle,
  FiUser,
  FiUsers,
} from 'react-icons/fi';
import {
  formatWhatToDoActionLabel,
  formatWhatToDoFeatureLabel,
} from '@/utils/landingPage/whatToDoSuggestedActions';
import {
  RESULT_OPTIONS_HEADER_DEFAULT,
} from './Landingpage.constants';
import {
  ResultOptionActionFeatureRow,
  ResultOptionChip,
  ResultOptionChipMeta,
  ResultOptionField,
  ResultOptionFieldLabel,
  ResultOptionFieldValue,
  ResultOptionOpenInChatButton,
  ResultOptionPill,
  ResultOptionPillIcon,
  ResultOptionWorkspaceRow,
  ResultOptionsChipList,
  ResultOptionsHeader,
  ResultOptionsSection,
} from './Landingpage.styles';
import type { WorkspaceDisplayInfo } from './Landingpage.types';
import {
  getActionPillVariant,
  getFeaturePillVariant,
  getWorkspacePillVariant,
  normalizeError,
} from './Landingpage.utils';

// ============= Types =============

export interface LandingPageResultOptionsChipsProps {
  options: ResultOption[];
  headerText?: string;
  getWorkspaceDisplayInfo: (
    workspaceId: string | undefined | null,
  ) => WorkspaceDisplayInfo;
  onExecuteOption: (option: ResultOption) => void | Promise<void>;
  onOpenInChat: (params: {
    workspaceId: string;
    promptText: string;
    defaultAskPromptId?: string;
  }) => Promise<void>;
  onOpenInChatError: (error: Error) => void;
  isOpeningInChat: boolean;
}

// ============= Component =============

export const LandingPageResultOptionsChips: React.FC<
  LandingPageResultOptionsChipsProps
> = ({
  options,
  headerText = RESULT_OPTIONS_HEADER_DEFAULT,
  getWorkspaceDisplayInfo,
  onExecuteOption,
  onOpenInChat,
  onOpenInChatError,
  isOpeningInChat,
}) => {
  if (!options.length) return null;

  return (
    <ResultOptionsSection>
      <ResultOptionsHeader>{headerText}</ResultOptionsHeader>
      <ResultOptionsChipList>
        {options.map((option, index) => {
          const action = String(option.action).toLowerCase();
          const feature = String(option.feature).toLowerCase();
          const { name: workspaceName, type: workspaceType } =
            getWorkspaceDisplayInfo(option.workspaceId);

          const actionLabel = formatWhatToDoActionLabel(action);
          const isAsk = feature === WhatToDoFeature.ASK;
          const actionPillVariant = getActionPillVariant(feature, action);
          const featurePillVariant = getFeaturePillVariant(feature);
          const featureLabel = formatWhatToDoFeatureLabel(feature);
          const workspacePillVariant = getWorkspacePillVariant(workspaceType);

          const description =
            option.description || 'No description provided.';
          const hasPrompt =
            typeof option.prompt === 'string' &&
            option.prompt.trim().length > 0;

          const ActionIcon =
            isAsk && action === WhatToDoAction.OPEN
              ? FiMessageCircle
              : isAsk && action === WhatToDoAction.CREATE
                ? FiMessageSquare
                : action === WhatToDoAction.CREATE
                  ? FiPlusCircle
                  : FiFolder;
          const FeatureIcon =
            featurePillVariant === 'feature-ask'
              ? FiMessageCircle
              : featurePillVariant === 'feature-collection'
                ? FiLayers
                : featurePillVariant === 'feature-document'
                  ? FiFileText
                  : featurePillVariant === 'feature-persona'
                    ? FiUser
                    : featurePillVariant === 'feature-prompt'
                      ? FiEdit3
                      : featurePillVariant === 'feature-workflow'
                        ? FiGitBranch
                        : FiBriefcase;
          const WorkspaceIcon =
            workspaceType === 'Private'
              ? FiLock
              : workspaceType === 'Collaborative'
                ? FiUsers
                : FiBriefcase;

          return (
            <ResultOptionChip
              key={`${option.workspaceId || 'no-ws'}-${option.id || index}-${index}`}
              role={hasPrompt ? undefined : 'button'}
              tabIndex={hasPrompt ? -1 : 0}
              data-disabled={hasPrompt ? 'true' : 'false'}
              aria-disabled={hasPrompt}
              onClick={
                hasPrompt
                  ? undefined
                  : () => {
                      void onExecuteOption(option);
                    }
              }
              onKeyDown={
                hasPrompt
                  ? undefined
                  : (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        void onExecuteOption(option);
                      }
                    }
              }
              title={description}
            >
              <ResultOptionActionFeatureRow>
                <ResultOptionPill $variant={actionPillVariant}>
                  <ResultOptionPillIcon>
                    <ActionIcon />
                  </ResultOptionPillIcon>
                  {actionLabel}
                </ResultOptionPill>
                <ResultOptionPill $variant={featurePillVariant}>
                  <ResultOptionPillIcon>
                    <FeatureIcon />
                  </ResultOptionPillIcon>
                  {featureLabel}
                </ResultOptionPill>
              </ResultOptionActionFeatureRow>

              <ResultOptionField>
                <ResultOptionFieldLabel>Description</ResultOptionFieldLabel>
                <ResultOptionFieldValue>{description}</ResultOptionFieldValue>
              </ResultOptionField>

              {workspaceName && (
                <ResultOptionField>
                  <ResultOptionWorkspaceRow>
                    <ResultOptionPill $variant={workspacePillVariant}>
                      <ResultOptionPillIcon>
                        <WorkspaceIcon />
                      </ResultOptionPillIcon>
                      {workspaceName}
                    </ResultOptionPill>
                  </ResultOptionWorkspaceRow>
                </ResultOptionField>
              )}

              {hasPrompt && option.workspaceId && (
                <ResultOptionField>
                  <ResultOptionFieldLabel>Useful prompt</ResultOptionFieldLabel>
                  <ResultOptionChipMeta>{option.prompt}</ResultOptionChipMeta>
                  <ResultOptionOpenInChatButton
                    type="button"
                    disabled={isOpeningInChat}
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (isOpeningInChat) return;
                      const promptText =
                        typeof option.prompt === 'string' ? option.prompt : '';
                      try {
                        await onOpenInChat({
                          workspaceId: option.workspaceId!,
                          promptText,
                          defaultAskPromptId:
                            feature === WhatToDoFeature.PROMPT
                              ? option.id
                              : undefined,
                        });
                      } catch (err: unknown) {
                        onOpenInChatError(
                          normalizeError(
                            err,
                            'Failed to open Chat session from result option',
                          ),
                        );
                      }
                    }}
                    title="Create a new Chat session and send this prompt"
                  >
                    {isOpeningInChat ? (
                      <>
                        <LoadingSpinner size="small" />
                        Opening…
                      </>
                    ) : (
                      <>
                        <FiMessageCircle />
                        Open in Chat
                      </>
                    )}
                  </ResultOptionOpenInChatButton>
                </ResultOptionField>
              )}
            </ResultOptionChip>
          );
        })}
      </ResultOptionsChipList>
    </ResultOptionsSection>
  );
};
